import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ShoppingBag, Menu, X } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { cn } from '@/lib/utils';

const NAV = [
  { to: '/', label: 'Home', end: true },
  { to: '/books', label: 'Books' },
  { to: '/apparel', label: 'Apparel' },
  { to: '/originals', label: 'Originals' },
  { to: '/studio', label: 'Prints' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
];

export const SiteHeader = () => {
  const { itemCount, openCart } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      className="sticky top-0 z-40 w-full bg-cream border-b border-ink/10"
      data-testid="site-header"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 h-36 lg:h-44 flex items-center justify-between gap-8">
        {/* Wordmark — official logo */}
        <Link
          to="/"
          className="flex items-center shrink-0"
          data-testid="brand-wordmark"
          aria-label="Graves Collective — Home"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_9cdf2342-a607-473f-8553-b1e4a60c73ce/artifacts/6el8ycim_Graves%20Collective%20Logo.png"
            alt="Graves Collective"
            className="h-36 lg:h-44 w-auto select-none mix-blend-multiply"
            draggable="false"
          />
        </Link>

        {/* Desktop nav */}
        <nav
          className="hidden lg:flex items-center gap-10"
          aria-label="Primary"
        >
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'text-xs uppercase tracking-[0.18em] transition-colors',
                  'hover:text-terracotta',
                  isActive ? 'text-ink' : 'text-ink/60'
                )
              }
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Cart + mobile toggle */}
        <div className="flex items-center gap-3">
          <button
            onClick={openCart}
            className="relative w-11 h-11 rounded-full hover:bg-ink/5 transition-colors flex items-center justify-center"
            aria-label={`Cart with ${itemCount} items`}
            data-testid="cart-toggle"
          >
            <ShoppingBag className="w-5 h-5 text-ink" strokeWidth={1.5} />
            {itemCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-terracotta text-cream text-[10px] font-medium rounded-full flex items-center justify-center">
                {itemCount > 99 ? '99+' : itemCount}
              </span>
            )}
          </button>

          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden w-11 h-11 rounded-full hover:bg-ink/5 transition-colors flex items-center justify-center"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? (
              <X className="w-5 h-5 text-ink" />
            ) : (
              <Menu className="w-5 h-5 text-ink" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <nav
          className="lg:hidden border-t border-ink/5 bg-cream"
          aria-label="Mobile primary"
          data-testid="mobile-nav"
        >
          <div className="px-6 sm:px-10 py-6 flex flex-col gap-5">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    'text-sm uppercase tracking-[0.18em] py-1',
                    isActive ? 'text-terracotta' : 'text-ink'
                  )
                }
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
};

export default SiteHeader;
